import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Box, Button, Typography, IconButton, LinearProgress, Divider, Chip,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FaFilePdf, FaEye, FaDownload, FaCut } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../Api";
import { openPdfWindow } from "../utils/pdfWindow";

// Keep localhost requests separate from the authenticated scan API client.
const macApi = axios.create({ baseURL: "http://127.0.0.1:8766", timeout: 60000 });
export default function FlipkartCropDialog({ open, onClose }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { summary, file, relpath, date }
  const [target, setTarget] = useState("mac");
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!job?.id || job.done) return undefined;
    let stopped = false;
    let timer;
    let failures = 0;
    const poll = async () => {
      try {
        const client = target === "mac" ? macApi : api;
        const route = target === "mac" ? `/api/flipkart/crop/${job.id}` : `/api/flipkart/crop/jobs/${job.id}`;
        const { data } = await client.get(route, { timeout: 15000 });
        if (stopped) return;
        failures = 0;
        setError("");
        setJob(data);
        if (data.done) {
          setBusy(false);
          if (!data.success || !data.relpath) setError(data.error || "Crop did not produce a saved PDF.");
          else {
            setResult({ ...data, local: target === "mac" });
            toast.success(`Done: ${data.summary?.kept_pages ?? "?"} labels kept.`);
          }
        } else timer = setTimeout(poll, 1500);
      } catch (err) {
        if (stopped) return;
        failures += 1;
        setError(err.response?.data?.error || "Cannot reach the crop job. Reconnecting...");
        if (failures >= 8 || err.response?.status === 404) {
          setBusy(false);
          setJob((current) => ({ ...current, done: true }));
          setError("Lost contact with the crop job. Check Files before starting another crop.");
        } else timer = setTimeout(poll, 3000);
      }
    };
    poll();
    return () => { stopped = true; clearTimeout(timer); };
  }, [job?.id, job?.done, target]);

  const reset = () => { setFile(null); setResult(null); setJob(null); setError(""); setBusy(false); };
  const close = () => { if (!busy) { reset(); onClose(); } };

  const run = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    setJob(null);
    setError("");
    try {
      let data;
      if (target === "mac") {
        const pdf = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = () => reject(new Error("Cannot read this PDF."));
          reader.readAsDataURL(file);
        });
        ({ data } = await macApi.post("/api/flipkart/crop", { pdf }));
      } else {
        const fd = new FormData();
        fd.append("file", file);
        ({ data } = await api.post("/api/flipkart/crop?async=1", fd, { timeout: 60000 }));
      }
      if (!data.id) throw new Error("The crop service did not return a job ID.");
      setJob(data);
    } catch (err) {
      setError(err?.response?.data?.error || (target === "mac"
        ? "Cannot reach the Mac console at 127.0.0.1:8766. Open the Mac crop tool below, or allow this site's local-network access if Chrome asks."
        : err.message || "Could not start the server crop."));
      setBusy(false);
    }
  };

  const openFile = async (asDownload) => {
    if (!result?.relpath) return;
    // For "Open", claim the tab synchronously so the popup blocker can't drop it.
    const win = asDownload ? null : openPdfWindow();
    try {
      const { data } = await (result.local ? macApi : api).get("/api/archive/file", {
        params: { p: result.relpath, ...(asDownload ? { dl: 1 } : {}) },
        responseType: "blob",
      });
      const blob = new Blob([data], { type: "application/pdf" });
      if (asDownload) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = result.file; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        win.show(blob, result.file);
      }
    } catch {
      if (win) win.fail();
      toast.error("Could not open the file.");
    }
  };

  const s = result?.summary || {};
  const g = s.groups || {};

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 800 }}>
        <span><FaCut style={{ marginRight: 8, verticalAlign: "-2px" }} />Flipkart Crop &amp; De-duplicate</span>
        <IconButton onClick={close} size="small" disabled={busy}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {target === "mac" ? "Save to this Mac's Flipkart files." : "Save to the server's Flipkart archive."}
        </Typography>
        <ToggleButtonGroup exclusive value={target} size="small" disabled={busy}
          onChange={(_, value) => { if (value) { setTarget(value); setResult(null); setJob(null); setError(""); } }} sx={{ mb: 2 }}>
          <ToggleButton value="mac">This Mac</ToggleButton>
          <ToggleButton value="server">Server</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Button variant="outlined" component="label" startIcon={<FaFilePdf />} disabled={busy}>
            Browse PDF
            <input hidden type="file" accept="application/pdf,.pdf"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setJob(null); setError(""); }} />
          </Button>
          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
            {file ? file.name : "No file selected"}
          </Typography>
        </Box>

        <Button
          variant="contained" sx={{ mt: 2, fontWeight: 700 }}
          onClick={run} disabled={!file || busy}
          startIcon={<FaCut />}
        >
          {busy ? "Processing…" : "Crop & De-duplicate"}
        </Button>

        {busy && <LinearProgress sx={{ mt: 2 }} />}
        {busy && job && <Typography variant="body2" sx={{ mt: 1 }}>
          {job.status}{job.total ? `: page ${job.current || 0} of ${job.total}` : ""}
        </Typography>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {target === "mac" && <Button href="http://127.0.0.1:8766/?tool=crop" target="_blank" rel="noopener" sx={{ mt: 1 }}>Open Mac Crop Tool</Button>}

        {result && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
              <Chip color="success" label={`${s.kept_pages ?? "?"} labels kept`} />
              <Chip color={s.duplicates_removed ? "warning" : "default"}
                label={`${s.duplicates_removed ?? 0} duplicates removed`} />
              <Chip variant="outlined" label={`${s.total_pages ?? "?"} pages in`} />
            </Box>
            {!result.local && <Typography variant="body2" sx={{ mb: 0.5 }}>
              <b>Woostro / Restore:</b> {s.woostro_restore_total ?? 0}
              {g.woostro_restore && ` (29=${g.woostro_restore["29"]}, 26=${g.woostro_restore["26"]}, other=${g.woostro_restore.other})`}
            </Typography>}
            {!result.local && <Typography variant="body2" sx={{ mb: 1.5 }}>
              <b>SLW:</b> {s.slw_total ?? 0}
              {g.slw && ` (29=${g.slw["29"]}, 26=${g.slw["26"]}, other=${g.slw.other})`}
            </Typography>}
            {s.sellers && Object.keys(s.sellers).length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                Sellers: {Object.entries(s.sellers).map(([k, v]) => `${k}=${v}`).join(", ")}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {result.local ? `Saved on this Mac: ${result.path}` : `Saved to archive: Flipkart / ${result.date} / ${result.file}`}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="contained" startIcon={<FaEye />}
                {...(result.local ? {
                  component: "a", href: `${macApi.defaults.baseURL}/api/archive/file?p=${encodeURIComponent(result.relpath)}`,
                  target: "_blank", rel: "noopener",
                } : { onClick: () => openFile(false) })}>
                Open PDF
              </Button>
              <Button size="small" variant="outlined" startIcon={<FaDownload />} onClick={() => openFile(true)}>
                Download
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
